import { Close, CloudUpload } from "@mui/icons-material";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import React, { useRef, useState } from "react";
import { CustomButton } from "src/Components/Common/Button/CustomButton";
import { DIALOG_MAX_WIDTHS } from "src/Components/Common/Modal/ModalWidth";

const VpUploadDocument = ({
  openVpUploadDocument,
  setOpenVpUploadDocument,
}: {
  openVpUploadDocument: boolean;
  setOpenVpUploadDocument: (open: boolean) => void;
}) => {
  const [dragActive, setDragActive] = useState<boolean>(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleCloseVpUploadDocument = () => {
    setOpenVpUploadDocument(false);
    setUploadedFiles([]);
  };

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (files: FileList) => {
    const newFiles = Array.from(files).filter(
      (file: File) =>
        file.type === "application/vnd.ms-excel" ||
        file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "text/csv" ||
        file.name.endsWith(".xls") ||
        file.name.endsWith(".xlsx") ||
        file.name.endsWith(".csv")
    );
    setUploadedFiles((prev) => [...prev, ...newFiles]);
  };

  const handleSelectFiles = () => {
    fileInputRef.current?.click();
  };

  const handleDownloadSample = () => {
    // Implement download sample format logic
    console.log("Download sample format");
  };

  return (
    <div>
      <Dialog open={openVpUploadDocument} maxWidth={DIALOG_MAX_WIDTHS.SM} fullWidth>
        <DialogTitle className="dialog-title">
          <div className="heading-m">Upload Documents</div>
          <div className="vertical_center_align">
            <IconButton onClick={handleCloseVpUploadDocument}>
              <Close />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Box
            sx={{
              border: `2px dashed ${dragActive ? "#541c4c" : "rgba(0,0,0,0.12)"}`,
              borderRadius: 2,
              backgroundColor: "#fafafa",
              padding: 4,
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                borderColor: "#541c4c",
                backgroundColor: "#f5f5f5",
              },
            }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={handleSelectFiles}
          >
            {/* Upload Icon */}
            <Box
              sx={{
                width: 142,
                height: 142,
                margin: "0 auto 24px",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  backgroundColor: "#fffcff",
                  border: "1px solid #eee5ed",
                  boxShadow: "0px 0px 28px 6px rgba(212,123,199,0.3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CloudUpload
                  sx={{
                    fontSize: 30,
                    color: "#541c4c",
                  }}
                />
              </Box>
            </Box>

            {/* Text Content */}
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 600,
                fontSize: "16px",
                lineHeight: 1.3,
                color: "rgba(0,0,0,0.87)",
                letterSpacing: "0.024px",
                marginBottom: 1,
              }}
            >
              Drag and drop sheet
            </Typography>
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: 1.4,
                color: "rgba(0,0,0,0.6)",
                letterSpacing: "0.035px",
                marginBottom: 3,
              }}
            >
              XLS/CSV formats supported
            </Typography>

            {/* Buttons */}
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
              <CustomButton
                size="medium"
                variant="outlined"
                sx={{
                  borderColor: "rgba(84,28,76,0.5)",
                  color: "#541c4c",
                  fontWeight: 600,
                  fontSize: "14px",
                  letterSpacing: "0.014px",
                  "&:hover": {
                    borderColor: "#541c4c",
                    backgroundColor: "rgba(84,28,76,0.04)",
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectFiles();
                }}
              >
                Select from this device
              </CustomButton>
              <Button
                variant="text"
                sx={{
                  color: "#541c4c",
                  fontWeight: 600,
                  fontSize: "14px",
                  letterSpacing: "0.014px",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "rgba(84,28,76,0.04)",
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownloadSample();
                }}
              >
                Download Sample Format
              </Button>
            </Box>
          </Box>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".xls,.xlsx,.csv"
            onChange={handleChange}
            style={{ display: "none" }}
          />

          {/* Display uploaded files */}
          {uploadedFiles.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Uploaded Files:
              </Typography>
              {uploadedFiles.map((file, index) => (
                <Typography key={index} variant="body2" sx={{ color: "rgba(0,0,0,0.6)" }}>
                  {file.name} ({Math.round(file.size / 1024)} KB)
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions className="dialog-actions">
          <CustomButton size="medium" colorToken="violet" variant="outlined" onClick={handleCloseVpUploadDocument}>
            Cancel
          </CustomButton>
          <CustomButton
            size="medium"
            colorToken="violet"
            variant="contained"
            disabled={uploadedFiles?.length === 0}
            onClick={() => {
              // Handle upload logic here
              console.log("Uploading files:", uploadedFiles);
              handleCloseVpUploadDocument();
            }}
          >
            Upload
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default VpUploadDocument;
